import { CAPTURE_ZIP, installedCaptureVersion, isNewer, useOfferedCaptureVersion } from "../lib/capture";
import { Spinner } from "../components/Spinner";

/* How to install the browser button, EVI Capture.
 *
 * Chrome loads it as an unpacked extension from a folder, so it does not
 * update itself. The steps keep one folder for good, because a new folder is a
 * new extension to Chrome: it starts signed out and the old copy stays. */
export function BrowserButton() {
  const offered = useOfferedCaptureVersion();
  const installed = installedCaptureVersion();

  return (
    <main className="prose">
      <h1>EVIE Capture</h1>
      <p className="muted">Sends the page on your screen to EVIE, including pages behind a sign-in.</p>

      <div className="bar">
        {offered.isLoading ? <span className="muted"><Spinner />Checking the latest version.</span>
          : offered.isError ? <span className="refusal">The download is not available: {(offered.error as Error).message}</span>
          : <a className="button-link" href={CAPTURE_ZIP} download>Download version {offered.data}</a>}
        <span className="muted">
          {!installed ? "Not installed in this browser."
            : isNewer(offered.data, installed) ? `Installed: ${installed}. Update it with the steps below.`
            : `Installed: ${installed}. Up to date.`}
        </span>
      </div>

      <h2>Install on Windows</h2>
      <ol>
        <li>Download the zip above.</li>
        <li>In Downloads, right-click <b>evie-capture.zip</b> and choose <b>Extract All</b>.</li>
        <li>Change the folder to <b>Documents\evie-capture</b> and choose <b>Extract</b>.</li>
        <li>In Chrome, type <b>chrome://extensions</b> in the address bar and press Enter.</li>
        <li>Turn on <b>Developer mode</b>, top right.</li>
        <li>Choose <b>Load unpacked</b> and pick the <b>evie-capture</b> folder in Documents.</li>
        <li>Click the puzzle piece in the toolbar and pin <b>EVIE Capture</b>.</li>
        <li>Click the EVIE icon and sign in with your EVIE Sales email and password.</li>
      </ol>

      <h2>Install on a Mac</h2>
      <ol>
        <li>Download the zip above.</li>
        <li>In Downloads, double-click <b>evie-capture.zip</b>. It makes a folder named <b>evie-capture</b>.</li>
        <li>Move the <b>evie-capture</b> folder to Documents.</li>
        <li>In Chrome, type <b>chrome://extensions</b> in the address bar and press Return.</li>
        <li>Turn on <b>Developer mode</b>, top right.</li>
        <li>Choose <b>Load unpacked</b> and pick the <b>evie-capture</b> folder in Documents.</li>
        <li>Click the puzzle piece in the toolbar and pin <b>EVIE Capture</b>.</li>
        <li>Click the EVIE icon and sign in with your EVIE Sales email and password.</li>
      </ol>

      <h2>Send a page</h2>
      <ul>
        <li>Click the EVIE icon and choose <b>Send this page</b>.</li>
        <li>Or press <b>Ctrl+Shift+E</b> on Windows, or <b>⌘⇧E</b> on a Mac.</li>
        <li>Or right-click the page and choose <b>Send this page to EVIE</b>.</li>
      </ul>
      <p>The EVIE Capture popup lists each page you sent and the records found in it.</p>

      <h2>Update it</h2>
      <ol>
        <li>Download the new zip above.</li>
        <li>Put its files in the <b>evie-capture</b> folder in Documents, replacing the old ones. On Windows, extract to that folder. On a Mac, open the new folder and drag its files in.</li>
        <li>In <b>chrome://extensions</b>, click the reload arrow on the <b>EVIE Capture</b> card.</li>
      </ol>
      <p className="muted">Use the same folder each time. A new folder installs a second copy, signed out.</p>
    </main>
  );
}
